import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState } from 'react';
import { X, Calendar, Users, Wallet, CheckCircle, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import AmountInput from './AmountInput';

const CashWizard = ({ isOpen, onClose, onSubmit, users, editMode = false, initialData = null, currentUser: currentUserProp }) => {
  const [currentStep, setCurrentStep] = useState(editMode ? 4 : 1);
  // Use currentUser from props (fresh from App state) — fallback to sessionStorage only if not provided
  const currentUser = currentUserProp || JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user') || 'null');

  // Filtrer les utilisateurs selon le rôle — vérifie les permissions directement depuis les props
  const getFilteredUsers = () => {
    if (!users || users.length === 0) return [];

    const role = currentUser?.role;
    const perms = currentUser?.granular_permissions || {};

    // Rôles privilégiés : voient tout
    if (['admin', 'super_admin', 'owner', 'gerant'].includes(role)) return users;

    // Permission granulaire : closures.view_all_users ou caisse.entrees.view_all_users
    if (perms?.caisse?.entrees?.view_all_users === true || perms?.closures?.view_all_users === true) return users;

    // Permission granulaire : accès au module users
    const usersPerms = perms?.users;
    if (usersPerms && Object.values(usersPerms).some(v => v === true)) return users;

    // Autres : seulement leur propre profil
    return users.filter(u => u.id === currentUser?.id);
  };

  const filteredUsers = getFilteredUsers();

  const [formData, setFormData] = useState(
    editMode && initialData
      ? {
          user_id: initialData.user_id || '',
          date: initialData.date ? parseSafeDate(initialData.date) : new Date(),
          montant_caissier: initialData.montant_caissier || 0,
          montant_reel: initialData.montant_reel != null ? initialData.montant_reel : '',
          notes: initialData.notes || ''
        }
      : {
          user_id: '',
          date: new Date(),
          montant_caissier: 0,
          montant_reel: '',
          notes: ''
        }
  );

  const isValidatedEdit = !!(editMode && initialData && initialData.status === 'validated');

  // Track opening state safely without arbitrary render limits
  const wasOpenRef = React.useRef(false);
  
  // Réinitialiser ou pré-remplir selon le mode quand le wizard s'ouvre
  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      if (editMode && initialData) {
        setCurrentStep(4);
        setFormData({
          user_id: initialData.user_id || '',
          date: initialData.date ? parseSafeDate(initialData.date) : new Date(),
          montant_caissier: initialData.montant_caissier || 0,
          montant_reel: initialData.montant_reel != null ? initialData.montant_reel : '',
          notes: initialData.notes || ''
        });
      } else {
        const isAdminUser = ['admin', 'super_admin', 'owner', 'gerant'].includes(currentUser?.role);
        const defaultUserId = !isAdminUser ? (currentUser?.id || '') : '';
        
        setCurrentStep(1);
        setFormData({
          user_id: defaultUserId,
          date: new Date(),
          montant_caissier: 0,
          montant_reel: '',
          notes: ''
        });
      }
    } else if (!isOpen) {
      wasOpenRef.current = false;
    }
  }, [isOpen, editMode, initialData, currentUser]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const handleUserSelect = (userId) => {
    setFormData({ ...formData, user_id: userId });
  };

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date });
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDayOfWeek = getDay(monthStart);
    
    // Jours de la semaine commençant par Samedi
    const weekDays = ['S', 'D', 'L', 'M', 'M', 'J', 'V'];
    
    // Ajuster pour commencer par Samedi
    const adjustedStartDay = (startDayOfWeek + 1) % 7;
    
    return (
      <div className="bg-gray-50 rounded-2xl p-6">
        {/* Header du calendrier */}
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ChevronLeft size={20} />
          </button>
          <h4 className="text-xl font-bold text-gray-900 capitalize">
            {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
          </h4>
          <button
            type="button"
            onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Jours de la semaine */}
        <div className="grid grid-cols-7 gap-2 mb-3">
          {weekDays.map((day, idx) => (
            <div key={idx} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Jours du mois */}
        <div className="grid grid-cols-7 gap-2">
          {/* Cellules vides pour aligner le premier jour */}
          {Array.from({ length: adjustedStartDay }).map((_, idx) => (
            <div key={`empty-${idx}`} />
          ))}
          
          {/* Jours du mois */}
          {daysInMonth.map((day) => {
            const isSelected = formData.date && 
              format(day, 'yyyy-MM-dd') === format(formData.date, 'yyyy-MM-dd');
            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
            
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => handleDateSelect(day)}
                className={`
                  h-12 rounded-xl font-medium text-base transition-all
                  ${isSelected
                    ? 'bg-blue-500 text-white'
                    : isToday
                    ? 'bg-blue-100 text-blue-900 border-2 border-blue-300'
                    : 'text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>

        {/* Date sélectionnée */}
        {formData.date && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-1">Date sélectionnée</p>
            <p className="text-lg font-bold text-gray-900 capitalize">
              {format(formData.date, 'EEEE dd MMM. yyyy', { locale: fr })}
            </p>
          </div>
        )}
      </div>
    );
  };

  const handleAmountChange = (value) => {
    setFormData({ ...formData, montant_caissier: value });
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    const submitData = { ...formData };
    if (editMode && initialData) {
      submitData._editId = initialData.id;
    }
    onSubmit(submitData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      user_id: '',
      date: new Date(),
      montant_caissier: 0,
      notes: ''
    });
    setCurrentStep(1);
    onClose();
  };

  if (!isOpen) return null;

  const selectedUser = (users || []).find(u => u.id === formData.user_id);
  const today = format(new Date(), 'dd MMM. yyyy', { locale: fr });

  // Fonction pour obtenir les initiales
  const getInitials = (fullName) => {
    if (!fullName) return '??';
    const parts = fullName.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Wallet className="text-blue-600" size={24} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Nouvelle entrée de caisse</h2>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">{today}</span>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between max-w-xl mx-auto">
            {[
              { step: 1, label: 'Caissier' },
              { step: 2, label: 'Date' },
              { step: 3, label: 'Montant' },
              { step: 4, label: 'Validation' }
            ].map(({ step, label }, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      step < currentStep
                        ? 'bg-blue-500 text-white'
                        : step === currentStep
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {step < currentStep ? '✓' : step}
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      step <= currentStep ? 'text-blue-600' : 'text-gray-400'
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {index < 3 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      step < currentStep ? 'bg-blue-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Étape 1 - Sélection du caissier */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Qui effectue la saisie ?</h3>
                <p className="text-gray-500">Sélectionnez le caissier responsable</p>
              </div>

              <div className="space-y-3">
                {filteredUsers && filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const isSelected = formData.user_id === user.id;
                    const initials = getInitials(user.full_name || user.username);
                    
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleUserSelect(user.id)}
                        className={`w-full p-3 rounded-xl border-2 transition-all flex items-center gap-4 ${
                          isSelected
                            ? 'bg-blue-50 border-blue-300 border-2'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-14 h-14 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-800 font-bold text-lg">{initials}</span>
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-bold text-lg text-gray-900">{user.full_name || user.username}</div>
                          <div className="text-gray-600 capitalize">{user.role || 'Caissier'}</div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-center py-8">Aucun utilisateur disponible</p>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <span />
                <span className="text-xs text-gray-500 font-medium">Étape 1 sur 4</span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!formData.user_id}
                  className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Étape 2 - Sélection de la date */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Quelle date ?</h3>
                <p className="text-gray-500">Choisissez la date de l'entrée de caisse</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase">Date</label>
                {renderCalendar()}
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Retour
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 2 sur 4</span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!formData.date}
                  className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Étape 3 - Montant */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Montant de la caisse</h3>
                <p className="text-gray-500">Tapez directement ou utilisez le pavé</p>
              </div>

              <AmountInput value={formData.montant_caissier} onChange={handleAmountChange} accentColor="blue" />

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">Notes (optionnel)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Commentaires..."
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 resize-none"
                  rows="3"
                />
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Retour
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 3 sur 4</span>
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Étape 4 - Validation */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Tout est correct ?</h3>
                <p className="text-gray-500">Relisez avant de valider</p>
              </div>

              {/* Résumé */}
              <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Caissier</span>
                  <span className="font-semibold text-gray-900">{selectedUser?.full_name || selectedUser?.username}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Date</span>
                  <span className="font-semibold text-gray-900 capitalize">
                    {formData.date && format(formData.date, 'd MMM. yyyy', { locale: fr })}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-600">Montant caisse</span>
                  <span className="font-bold text-2xl text-blue-600">
                    {formData.montant_caissier.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA
                  </span>
                </div>
              </div>

              {/* Montant Réel — éditable uniquement pour les entrées déjà validées */}
              {isValidatedEdit && (
                <div className="bg-green-50/60 border border-green-200 rounded-xl p-4 space-y-2" data-testid="cash-wizard-montant-reel">
                  <label className="text-sm font-semibold text-green-900">Montant réel validé (DA)</label>
                  <p className="text-xs text-green-700/80 mb-2">Vous pouvez ajuster le montant réel de cette caisse déjà validée.</p>
                  <AmountInput
                    value={formData.montant_reel}
                    onChange={(value) => setFormData({ ...formData, montant_reel: value })}
                    accentColor="green"
                  />
                </div>
              )}

              {/* Message d'info */}
              <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0" size={24} />
                <span className="text-blue-700 font-medium">{isValidatedEdit ? 'Mise à jour de la caisse validée' : 'Sera enregistrée dans votre tableau de bord'}</span>
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Modifier
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 4 sur 4</span>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-colors"
                >
                  <CheckCircle size={16} />
                  Enregistrer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashWizard;
